// Pipeline Progress panel — anchored over #custom-chat (MexaScreen production chat).
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { STEP_COLORS, STEP_LABELS, } from './langGraphChatTypes';
import PipelineGraphView from './langGraphPipelineGraph';
import { findTraceForStep, resolveSelectedStepId, } from './langGraphPipelineTree';
/** Bubble panel — slightly wider left, inset from chat edges (not flush corner). */
const BUBBLE = {
    leftOverflow: 28,
    topInset: 10,
    bottomInset: 14,
    rightInset: 10,
};
function formatStateSnapshot(data) {
    if (!(data === null || data === void 0 ? void 0 : data.state_snapshot))
        return '{}';
    try {
        return JSON.stringify(data.state_snapshot, null, 2);
    }
    catch (_a) {
        return String(data.state_snapshot);
    }
}
function measureChatAnchor() {
    const chat = document.getElementById('custom-chat');
    if (!chat)
        return null;
    const rect = chat.getBoundingClientRect();
    return {
        top: rect.top + BUBBLE.topInset,
        left: rect.left - BUBBLE.leftOverflow,
        width: rect.width + BUBBLE.leftOverflow - BUBBLE.rightInset,
        height: rect.height - BUBBLE.topInset - BUBBLE.bottomInset,
    };
}
export default function PipelineDebugDrawer({ open, onClose, data }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const [selectedStepId, setSelectedStepId] = useState(null);
    const [anchor, setAnchor] = useState(null);
    const currentStep = data === null || data === void 0 ? void 0 : data.current_step;
    const effectiveSelection = resolveSelectedStepId(currentStep, selectedStepId);
    useLayoutEffect(() => {
        if (!open)
            return;
        const sync = () => setAnchor(measureChatAnchor());
        sync();
        const chat = document.getElementById('custom-chat');
        const ro = chat ? new ResizeObserver(sync) : null;
        ro === null || ro === void 0 ? void 0 : ro.observe(chat);
        window.addEventListener('resize', sync);
        window.addEventListener('scroll', sync, true);
        return () => {
            ro === null || ro === void 0 ? void 0 : ro.disconnect();
            window.removeEventListener('resize', sync);
            window.removeEventListener('scroll', sync, true);
        };
    }, [open]);
    useEffect(() => {
        if (!open)
            return;
        setSelectedStepId(currentStep !== null && currentStep !== void 0 ? currentStep : null);
    }, [open, currentStep]);
    if (!open || !anchor)
        return null;
    const phase = (_a = data === null || data === void 0 ? void 0 : data.phase) !== null && _a !== void 0 ? _a : '—';
    const projectId = (_b = data === null || data === void 0 ? void 0 : data.project_id) !== null && _b !== void 0 ? _b : '—';
    const userId = (_c = data === null || data === void 0 ? void 0 : data.user_id) !== null && _c !== void 0 ? _c : null;
    const msSessionId = (_d = data === null || data === void 0 ? void 0 : data.ms_session_id) !== null && _d !== void 0 ? _d : null;
    const runLog = [...((_e = data === null || data === void 0 ? void 0 : data.run_log) !== null && _e !== void 0 ? _e : [])].reverse();
    const selectedTrace = findTraceForStep(data === null || data === void 0 ? void 0 : data.pipeline_trace, effectiveSelection);
    const lastTrace = selectedTrace !== null && selectedTrace !== void 0 ? selectedTrace : (((_f = data === null || data === void 0 ? void 0 : data.pipeline_trace) === null || _f === void 0 ? void 0 : _f.length)
        ? data.pipeline_trace[data.pipeline_trace.length - 1]
        : null);
    const handleClose = (e) => {
        e.stopPropagation();
        onClose();
    };
    return (React.createElement("div", { className: "lg-debug-backdrop", onClick: onClose, role: "presentation" },
        React.createElement("aside", { className: "lg-debug-drawer lg-debug-drawer-chat", style: {
                top: anchor.top,
                left: anchor.left,
                width: anchor.width,
                height: anchor.height,
            }, onClick: (e) => e.stopPropagation(), role: "dialog", "aria-label": "Pipeline Progress", "aria-modal": "true" },
            React.createElement("header", { className: "lg-debug-header" },
                React.createElement("span", { className: "modal-close-spacer", "aria-hidden": "true" }),
                React.createElement("div", { className: "header-title" },
                    React.createElement("h3", { className: "lg-debug-header-title" }, "Pipeline Progress")),
                React.createElement("button", { type: "button", className: "modal-close", onClick: handleClose, "aria-label": "Close" },
                    React.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true" },
                        React.createElement("line", { x1: "18", y1: "6", x2: "6", y2: "18" }),
                        React.createElement("line", { x1: "6", y1: "6", x2: "18", y2: "18" })))),
            React.createElement("div", { className: "lg-debug-body" },
                React.createElement("section", { className: "lg-debug-section lg-debug-section-pipeline" },
                    React.createElement("h4", { className: "lg-debug-section-title" }, "Workflow"),
                    React.createElement(PipelineGraphView, { data: data, selectedStepId: effectiveSelection, onSelectStep: setSelectedStepId }),
                    !((_g = data === null || data === void 0 ? void 0 : data.step_progress) === null || _g === void 0 ? void 0 : _g.length) && (React.createElement("p", { className: "lg-debug-empty lg-pipeline-empty-hint" }, "Expand stages as the pipeline runs \u2014 active step glows softly."))),
                React.createElement("section", { className: "lg-debug-section" },
                    React.createElement("h4", { className: "lg-debug-section-title" }, "Current Step"),
                    React.createElement("div", { className: "lg-trace-card" }, lastTrace ? (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: "lg-trace-step-name", style: { color: (_h = STEP_COLORS[lastTrace.step]) !== null && _h !== void 0 ? _h : '#3B5FFF' } }, (_j = STEP_LABELS[lastTrace.step]) !== null && _j !== void 0 ? _j : lastTrace.step),
                        React.createElement("pre", { className: "lg-trace-message" }, lastTrace.message))) : (React.createElement("p", { className: "lg-debug-empty" }, effectiveSelection
                        ? `No trace yet for ${(_k = STEP_LABELS[effectiveSelection]) !== null && _k !== void 0 ? _k : effectiveSelection}.`
                        : 'Select a step in the workflow tree.')))),
                React.createElement("section", { className: "lg-debug-section lg-debug-section-debug" },
                    React.createElement("h4", { className: "lg-debug-section-title" }, "Debug"),
                    React.createElement("p", { className: "lg-debug-subheading" }, "MexaState"),
                    React.createElement("pre", { className: "lg-state-json" }, formatStateSnapshot(data)),
                    React.createElement("p", { className: "lg-debug-subheading" }, "Run log"),
                    React.createElement("div", { className: "lg-run-log" }, runLog.length
                        ? runLog.map((line, i) => (React.createElement("div", { key: `${i}-${line.slice(0, 24)}`, className: "lg-run-log-line" }, line)))
                        : React.createElement("p", { className: "lg-debug-empty" }, "(no log)")))),
            React.createElement("footer", { className: "lg-debug-footer" },
                React.createElement("span", { title: userId ? `Cognito sub: ${userId}` : 'sub non disponible (demo / pas de token)' },
                    "user: ",
                    userId ? `${userId.slice(0, 8)}…` : '—'),
                React.createElement("span", { title: `MexaScreen sessionId (EFS: username-${msSessionId !== null && msSessionId !== void 0 ? msSessionId : '?'})` },
                    "session: ", msSessionId !== null && msSessionId !== void 0 ? msSessionId : '—'),
                React.createElement("span", { title: `LangGraph thread_id = sessionId (${projectId})` },
                    "thread: ",
                    projectId),
                React.createElement("span", null,
                    "step: ", currentStep !== null && currentStep !== void 0 ? currentStep : '—'),
                React.createElement("span", null,
                    "phase: ",
                    phase)))));
}
